import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Spacer,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  useColorMode,
} from '@chakra-ui/react';
import {
  ColumnDef,
  SortingState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';

import {
  TaskFilterGQLType,
  TaskFragmentGQLType,
  TaskStatusGQLType,
  useGetOrganizationQuery,
  useGetTasksBySectionQuery,
  useGetTasksInitialFiltersQuery,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  statusColorScheme,
  statusIcon,
  taskStatuses,
  getActionRequiredFromCategorySpecificProperties,
  getTrucatedLoanIdsFromCategorySpecificProperties,
  UNASSIGNED_USER,
} from './common';
import { DownloadTaskCenterButton } from './DownloadTaskCenterButton';
import { TaskDetailDrawer } from './TaskCenterDrawer';
import { TaskCenterFiltersDrawer } from './TaskCenterFilter';
import { Counter } from 'packages/common-frontend/components/Counter';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { remToPx, resolveColor } from 'packages/common-frontend/theme/utils';
import { formatCurrencyAmount } from 'packages/common-frontend/utils';
import { BulkArchiveTasks } from 'packages/tasks/ui/BulkArchiveTasks';
import { BulkAssignTasks } from 'packages/tasks/ui/BulkAssignTasks';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { BACKGROUND_COLORS } from '~/packages/common-frontend/theme/tokens';

type TaskSectionGroupByParams =
  | {
      type: 'status';
      status: TaskStatusGQLType;
    }
  | {
      type: 'user';
      userId: string;
      name: string;
    };

const getSectionKey = (params: TaskSectionGroupByParams) => {
  switch (params.type) {
    case 'status':
      return params.status;
    case 'user':
      return params.name;
    default:
      return finleyAssertUnreachable(params);
  }
};

const TaskTable = ({
  titleParams,
  tasks,
  downloadCurrentTableRef,
  // rowSelectionProps
  canSelectRows = false,
  rowSelection,
  setRowSelection,
}: {
  titleParams: TaskSectionGroupByParams;
  tasks: TaskFragmentGQLType[];
  downloadCurrentTableRef: RefObject<HTMLDivElement>;
  // rowSelectionProps
  canSelectRows: boolean;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
}) => {
  const columns = useMemo(() => {
    const tableColumns: ColumnDef<TaskFragmentGQLType, any>[] = [
      {
        id: 'selectColumn',
        size: remToPx(2),
        // eslint-disable-next-line react/display-name
        header: ({ table }) => (
          <Flex
            as="span"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Checkbox
              isChecked={table.getIsAllRowsSelected()}
              isIndeterminate={
                // we do not currently support rendering different table sections
                // so instead we are rendering separate tables.
                // "rowSelection" is state that is being shared across all these separate tables tables.
                // because of this & how tanstack implements "table.getIsSomeRowsSelected()" we CANNOT use that here.
                // this function will return WRONG behavior as it always returns true if `rowSelection` is not empty.
                // since rows from other tables can also be selected,
                // we need to check if any rows on THIS specific table are selected.
                !table.getIsAllRowsSelected() &&
                table.getSelectedRowModel().rows.length > 0
              }
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          </Flex>
        ),
        // eslint-disable-next-line react/display-name
        cell: ({ row }) => {
          if (!row.id) return null;

          return (
            <Flex
              as="span"
              onClick={(event) => {
                // need to wrap chakra checkboxes with a span component to prevent clicks from bubbling up.
                // in this case: we do not want selecting the checkbox to also open a drawer for the row
                event.stopPropagation();
              }}
            >
              <Checkbox
                isChecked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
            </Flex>
          );
        },
      },
      // these columns should also ALL exist in the `exportToXlsx` logic `DownloadTaskCenterButton.tsx`
      {
        id: 'status',
        accessorFn: (cell) => cell.taskEntity.status,
        enableSorting: true,
        size: remToPx(9),
        header: 'Status',
        // eslint-disable-next-line react/display-name
        cell: ({ getValue }) => {
          const status = getValue() as TaskStatusGQLType;
          return (
            <Tag
              variant="ghost"
              colorScheme={statusColorScheme[status]}
              size="sm"
            >
              <TagLeftIcon>
                <Icon icon={statusIcon[status]} />
              </TagLeftIcon>
              <TagLabel>{_.startCase(status)}</TagLabel>
            </Tag>
          );
        },
      },
      {
        id: 'creditFacilityName',
        accessorFn: (cell) =>
          _.startCase(cell.domainProperties.creditFacility.name),
        enableSorting: true,
        size: remToPx(13),
        header: 'Facility',
      },
      {
        id: 'loanId',
        accessorFn: (cell) =>
          getTrucatedLoanIdsFromCategorySpecificProperties(
            cell.domainProperties.categorySpecificProperties,
          ),
        enableSorting: true,
        size: remToPx(10),
        header: 'Loan',
      },
      {
        id: 'category',
        accessorFn: (cell) =>
          _.startCase(
            cell.domainProperties.categorySpecificProperties.category,
          ),
        header: 'Category',
        enableSorting: true,
        size: remToPx(14),
      },
      {
        id: 'actionRequired',
        accessorFn: (cell) =>
          getActionRequiredFromCategorySpecificProperties(
            cell.domainProperties.categorySpecificProperties,
          ),
        header: 'Action Required',
        enableSorting: true,
        size: remToPx(10),
      },
      {
        id: 'amount',
        accessorFn: (cell) => cell.domainProperties.amount,
        cell: (info) =>
          formatCurrencyAmount(info.getValue(), Bank.Currency.USD),
        header: 'Amount',
        enableSorting: true,
        size: remToPx(9),
      },
      {
        id: 'partyName',
        accessorFn: (cell) => _.startCase(cell.domainProperties.party.name),
        header: 'Party Name',
        enableSorting: true,
        size: remToPx(16),
      },
      {
        id: 'dueDate',
        accessorFn: (cell) => cell.taskEntity.dueDate,
        cell: (cell) =>
          commonDate.fullDateStrToShortMonthDayYearString(cell.getValue()),
        header: 'Due Date',
        enableSorting: true,
        size: remToPx(9),
      },
      {
        id: 'assignedUser',
        accessorFn: (cell) =>
          cell.taskEntity.assignedUser
            ? `${cell.taskEntity.assignedUser.firstName} ${cell.taskEntity.assignedUser.lastName}`
            : '-',
        enableSorting: true,
        size: remToPx(12),
        header: 'Assigned User',
      },
    ];

    return tableColumns;
  }, []);

  const columnVisibility = useMemo(() => {
    // note: columns not included in this map will automatially be visible

    return {
      selectColumn: canSelectRows,
      status: titleParams.type === 'user', // if user-view, show status column
      assignedUser: titleParams.type === 'status', // if status-view, show assigned user column
    };
  }, [titleParams.type, canSelectRows]);

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data: tasks,
    columns,
    enableMultiSort: true,
    state: { sorting, rowSelection: rowSelection || {} },
    initialState: {
      sorting,
      columnVisibility,
    },
    getRowId: (row, index) => row.taskEntity?.id || `no-task-entity-${index}`,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
    excludeColumnIds: ['selectColumn'],
  });

  const [drawerTask, setDrawerTask] = useState<TaskFragmentGQLType>();

  const sectionKey = getSectionKey(titleParams);

  return (
    <>
      {downloadCurrentTableRef.current &&
        //  This portal is rendered in the parent component, TaskTableSection
        createPortal(
          <CsvXlsxDownloadSelect
            dataRows={csvDownload.dataRows}
            headers={csvDownload.headers}
            filenamePrefix={`task-center-${sectionKey}`}
          />,
          downloadCurrentTableRef.current,
        )}

      <TableBETA
        table={table}
        onRowClick={(row) => {
          setDrawerTask(row.original);
        }}
      />

      {drawerTask && (
        <TaskDetailDrawer
          selectedTask={drawerTask}
          onClose={() => {
            setDrawerTask(undefined);
          }}
        />
      )}
    </>
  );
};

const TaskTableSection = ({
  titleParams,
  filterState,
  // rowSelectionProps
  canSelectRows = false,
  rowSelection,
  setRowSelection,
}: {
  titleParams: TaskSectionGroupByParams;
  filterState: TaskFilterGQLType;
  // rowSelectionProps
  canSelectRows: boolean;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
}) => {
  const sectionKey = getSectionKey(titleParams);

  const downloadCurrentTableRef = useRef<HTMLDivElement>(null);

  const { __typename: _unused, ...filters } = filterState;

  const getTasksBySectionQuery = useGetTasksBySectionQuery({
    variables: {
      section:
        titleParams.type === 'status'
          ? {
              type: 'status',
              status: titleParams.status,
            }
          : {
              type: 'user',
              // if the user id === our fake unassigned user id then we actually want to pass null as the user id here
              assignedUserId:
                titleParams.userId === UNASSIGNED_USER
                  ? null
                  : titleParams.userId,
            },
      filters,
    },
  });

  if (getTasksBySectionQuery.error) throw getTasksBySectionQuery.error;

  // hide section if no tasks
  if (
    !getTasksBySectionQuery.data ||
    getTasksBySectionQuery.data.getTasksBySection.length === 0
  ) {
    return null;
  }

  if (getTasksBySectionQuery.loading) return <Skeleton height="400px" />;

  const tasks = getTasksBySectionQuery.data.getTasksBySection;

  return (
    <Stack key={sectionKey} gap="1rem">
      <AccordionItem
        width="full"
        style={{ borderTopWidth: '0px', borderBottomWidth: '0px' }}
      >
        {({ isExpanded }) => (
          <>
            <HStack padding="0.5rem">
              <AccordionButton width="full">
                <HStack gap="1rem" width="full">
                  <Typography variant="section-title" trim>
                    {_.startCase(sectionKey)}
                  </Typography>
                  <Counter
                    count={tasks.length}
                    colorScheme={
                      titleParams.type === 'status'
                        ? statusColorScheme[titleParams.status]
                        : 'gray'
                    }
                  />

                  <Spacer />
                  <AccordionIcon />
                </HStack>
              </AccordionButton>

              {isExpanded && (
                // The tanstack download button needs to be rendered with the table itself,
                // but we want to show it in the header here. The easiest way to do this was to
                // lift just this download component out of the child component through a portal.
                <Box
                  style={{ alignSelf: 'center' }}
                  ref={downloadCurrentTableRef}
                />
              )}
            </HStack>
            <AccordionPanel>
              <TaskTable
                titleParams={titleParams}
                tasks={tasks}
                downloadCurrentTableRef={downloadCurrentTableRef}
                canSelectRows={canSelectRows}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
              />
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Stack>
  );
};

// ******************************************************
// *                    Task Center                     *
// ******************************************************
export const TaskCenter = () => {
  const { user } = useUserAuthOrThrow();
  const { colorMode } = useColorMode();

  const isAdmin = user.roleType === 'ADMIN';

  const [groupByType, setGroupByType] = useState<
    TaskSectionGroupByParams['type']
  >('status');

  const [filterState, setFilterState] = useState<TaskFilterGQLType>();
  const initialFilterState = useGetTasksInitialFiltersQuery();

  if (initialFilterState.error) {
    throw initialFilterState.error;
  }

  useEffect(() => {
    if (initialFilterState.data) {
      setFilterState(
        initialFilterState.data.getTasksInitialFilters.currentFilters,
      );
    }
  }, [initialFilterState.data]);

  const allLoanIds = initialFilterState.data?.getTasksInitialFilters.allLoanIds;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // when we refetch data (updates "filterState") or change "groupByType",
  // we want to reset selected tasks to an empty map as the groupings have changed
  useEffect(() => {
    setRowSelection({});
  }, [groupByType, filterState]);

  const selectedTaskIds = useMemo(() => {
    // technically tanstack stores row selection as { rowId: boolean }.
    // the current implementation of tanstack actually only stores rowIds that are selected
    // (e.g. only stores rows where boolean is always true) and when you unselect,
    // the rowId disappears rather than gets set to false in RowSelectionState.
    // in case this changes, im writing the below code to only include rowIds that map to TRUE

    return new Set(
      Object.entries(rowSelection)
        .filter(([_id, bool]) => bool)
        .map(([id, _bool]) => id),
    );
  }, [rowSelection]);

  const getOrganization = useGetOrganizationQuery({
    skip: isAdmin === false,
    variables: { organizationId: user.organizationId },
  });

  const users = useMemo(() => {
    if (
      getOrganization.data?.organization &&
      getOrganization.data.organization.users.length > 0
    ) {
      const usersData = [...getOrganization.data.organization.users];

      usersData.sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`,
        ),
      );

      return usersData;
    }

    return null;
  }, [getOrganization.data?.organization]);

  if (!filterState || !initialFilterState.data || !allLoanIds) {
    return <Skeleton h="400px" />;
  }

  const initialFilters =
    initialFilterState.data.getTasksInitialFilters.currentFilters;

  return (
    <Box
      // removing the automatic page margins to create a sticky header
      // and manually re-apply page paddings for sticky header + tables below
      marginLeft="-2rem"
      marginTop="-2rem"
    >
      {/* Sticky header */}
      <Stack
        width="full"
        position="sticky"
        zIndex="sticky"
        top="0"
        paddingLeft="2rem"
        paddingTop="2rem"
        paddingBottom="1rem"
        backgroundColor={resolveColor(
          { colorMode, colorScheme: 'gray' },
          BACKGROUND_COLORS.white.base,
        )}
      >
        <HStack gap="1em" mb="1.5rem">
          <Typography variant="page-title" trim>
            Tasks
          </Typography>
          <Spacer />
          {isAdmin && (
            <Menu>
              <MenuButton
                as={Button}
                colorScheme="v2-aurora"
                variant="primary-muted"
              >
                Group By
              </MenuButton>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    setGroupByType('status');
                  }}
                >
                  Status
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setGroupByType('user');
                  }}
                >
                  User
                </MenuItem>
              </MenuList>
            </Menu>
          )}
          <TaskCenterFiltersDrawer
            key={JSON.stringify(filterState)}
            onFilterChange={async (newFilters) => {
              setFilterState(newFilters);
            }}
            initialFilter={{
              creditFacilityIds: initialFilters.creditFacilityIds,
              statuses: initialFilters.statuses,
              categories: initialFilters.categories,
              loanIds: initialFilters.loanIds ?? undefined,
              dateRange: initialFilters.dateRange ?? undefined,
            }}
            currentFilter={{
              creditFacilityIds: filterState.creditFacilityIds,
              statuses: filterState.statuses,
              categories: filterState.categories,
              loanIds: filterState.loanIds ?? undefined,
              dateRange: filterState.dateRange ?? undefined,
            }}
            allLoanIds={allLoanIds}
          />
          <DownloadTaskCenterButton
            groupByType={groupByType}
            filterState={filterState}
          />
        </HStack>
        {isAdmin && (
          <Flex
            direction="row"
            gap="1rem"
            alignItems="center"
            paddingX="1.5rem"
            paddingBottom="0.5rem"
          >
            <BulkAssignTasks
              taskIds={[...selectedTaskIds]}
              clearAll={() => {
                setRowSelection({});
              }}
              // sarah todo - need to support select all when each section is fetching its own data

              // selectAll={() => {
              //   const allTasks: RowSelectionState = {};

              //   Object.values(tasksByGroup)
              //     .flatMap((group) => group)
              //     .forEach((task) => {
              //       allTasks[task.taskEntity.id] = true;
              //     });

              //   setRowSelection(allTasks);
              // }}
              onSubmit={() => {
                setRowSelection({});
              }}
            />
            <BulkArchiveTasks
              taskIds={[...selectedTaskIds]}
              onSubmit={() => {
                setRowSelection({});
              }}
            />
          </Flex>
        )}
      </Stack>

      {/* Page content */}
      <Stack marginLeft="2rem">
        <Stack gap="2rem">
          <>
            {/* Each tasks group by section as a table within an accordion. */}
            <Accordion allowMultiple defaultIndex={[0]}>
              {(() => {
                switch (groupByType) {
                  case 'status': {
                    return taskStatuses.map((status) => {
                      if (
                        filterState.statuses &&
                        !filterState.statuses.includes(status)
                      ) {
                        return null;
                      }

                      return (
                        <TaskTableSection
                          key={status}
                          filterState={filterState}
                          titleParams={{ type: 'status', status }}
                          // row selection properties
                          canSelectRows={isAdmin}
                          rowSelection={isAdmin ? rowSelection : undefined}
                          setRowSelection={
                            isAdmin ? setRowSelection : undefined
                          }
                        />
                      );
                    });
                  }
                  case 'user':
                    return (
                      <>
                        {/* Unassigned */}
                        <TaskTableSection
                          key={UNASSIGNED_USER}
                          filterState={filterState}
                          titleParams={{
                            type: 'user',
                            userId: UNASSIGNED_USER,
                            name: `Unassigned`,
                          }}
                          // row selection properties
                          canSelectRows={isAdmin}
                          rowSelection={isAdmin ? rowSelection : undefined}
                          setRowSelection={
                            isAdmin ? setRowSelection : undefined
                          }
                        />

                        {/* Tasks for each user in org */}
                        {users &&
                          users.map(({ id: userId, firstName, lastName }) => {
                            return (
                              <TaskTableSection
                                key={userId}
                                filterState={filterState}
                                titleParams={{
                                  type: 'user',
                                  userId,
                                  name: `${firstName} ${lastName}`,
                                }}
                                // row selection properties
                                canSelectRows={isAdmin}
                                rowSelection={
                                  isAdmin ? rowSelection : undefined
                                }
                                setRowSelection={
                                  isAdmin ? setRowSelection : undefined
                                }
                              />
                            );
                          })}
                      </>
                    );
                  default:
                    return null;
                }
              })()}
            </Accordion>
          </>
        </Stack>
      </Stack>
    </Box>
  );
};
